import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Alert, Box, Snackbar } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { PasswordToSet } from '@codefluegel/wowis-rest-typescript-client';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import axios from 'axios';
import ThemedButton from '../button/ThemedButton';
import { useAuthApi } from '../../ApiClient';
import useMedia from '../../hooks/useMedia';

interface UpdatePassword {
  oldPassword: string;
  newPassword: string;
  newPasswordRepeat: string;
}

const UpdatePasswordModal = observer(
  ({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element => {
    const kundenApi = useAuthApi();
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const isMobile = useMedia('md');

    const { control, handleSubmit, getValues, setValue } =
      useForm<UpdatePassword>();

    const handleClose = () => {
      setValue('oldPassword', '');
      setValue('newPassword', '');
      setValue('newPasswordRepeat', '');
      onClose();
    };

    const saveEntry = async () => {
      try {
        const passwordToSet: PasswordToSet = {
          oldPassword: getValues('oldPassword'),
          newPassword: getValues('newPassword'),
        };
        await kundenApi.updatePassword(passwordToSet);
        handleClose();
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response?.data === 'Old password did not match!') {
            setSnackbarMessage('Das alte Passwort stimmt nicht überein!');
          } else {
            setSnackbarMessage(error.message);
          }
          setSnackbarOpen(true);
        }
      }
    };

    return (
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle color="secondary">Kennwort aktualisieren</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              paddingX: isMobile ? 0 : 10,
            }}
          >
            <Controller
              name="oldPassword"
              control={control}
              rules={{ required: 'Pflichtfeld!' }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  sx={{
                    marginTop: 2,
                    marginBottom: 2,
                    input: { color: '#58585A' },
                  }}
                  margin="dense"
                  id="content"
                  label="Altes Passwort"
                  type="password"
                  fullWidth
                  variant="standard"
                  inputProps={{
                    style: { color: '#58585A' },
                  }}
                  value={value}
                  error={!!error}
                  onChange={onChange}
                  helperText={error ? error.message : null}
                  required
                />
              )}
            />

            <Controller
              name="newPassword"
              control={control}
              rules={{
                minLength: {
                  value: 8,
                  message: '* Passwort muss mindestens 8 Zeichen lang sein',
                },
                validate: {
                  hasNumber: value =>
                    /\d/.test(value) ||
                    'Passwort muss mindestens eine Zahl enthalten',
                  hasCapitalLetter: value =>
                    /[A-Z]/.test(value) ||
                    'Passwort muss mindestens einen Großbuchstaben enthalten',
                  hasSpecialChar: value =>
                    /[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/.test(value) ||
                    'Passwort muss mindestens ein Sonderzeichen enthalten',
                  required: value => {
                    if (value === '') return 'Pflichtfeld!';
                    return true;
                  },
                },
              }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  sx={{
                    marginTop: 2,
                    marginBottom: 2,
                    input: { color: '#58585A' },
                  }}
                  margin="dense"
                  id="content"
                  label="Neues Passwort"
                  type="password"
                  fullWidth
                  variant="standard"
                  inputProps={{
                    style: { color: '#58585A' },
                  }}
                  value={value}
                  error={!!error}
                  onChange={onChange}
                  helperText={error ? error.message : null}
                  required
                />
              )}
            />

            <Controller
              name="newPasswordRepeat"
              control={control}
              rules={{
                validate: {
                  required: value => {
                    if (value === '') return 'Pflichtfeld!';
                    if (value !== getValues('newPassword')) {
                      return '* Passwörter stimmen nicht überein';
                    }
                    return true;
                  },
                },
              }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  sx={{
                    marginTop: 2,
                    marginBottom: 2,
                    input: { color: '#58585A' },
                  }}
                  margin="dense"
                  id="content"
                  label="Neues Passwort wiederholen"
                  type="password"
                  fullWidth
                  variant="standard"
                  inputProps={{
                    style: { color: '#58585A' },
                  }}
                  value={value}
                  error={!!error}
                  onChange={onChange}
                  helperText={error ? error.message : null}
                  required
                />
              )}
            />

            <DialogActions
              sx={{
                justifyContent: 'space-around',
                marginTop: 4,
                marginBottom: 2,
              }}
            >
              <Button sx={{ width: 180 }} onClick={handleClose}>
                Abbrechen
              </Button>
              <Box width={180}>
                <ThemedButton
                  label="Speichern"
                  onClick={handleSubmit(saveEntry)}
                />
              </Box>
            </DialogActions>
          </Box>
        </DialogContent>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>
    );
  },
);

export default UpdatePasswordModal;
